import { ComponentType } from "@angular/cdk/portal";

export type PresenterRegistryItem = 
{
    /**
    * A key to create an override template for a specific use case
    */
    key?: string,        

    /**
     * The model associated with the template
     */
    model: Function,

    /**
     * The template
     */
    component: ComponentType<unknown>
}

export type PresenterModelKey = 
{
   /**
    * The model to associate with the presenter
    */
   model: Function,
   /**
    * A key to create an override template for a specific use case
    */
   key?: string, 
}


/**
 * @summary Register a presenter for base types
 */
export function PresenterRegistration(input: { key?: string, types: Function[] }) {
  return function (target: ComponentType<unknown>) {
    for (const type of input.types) {
      PresenterHelper.register({ key: input.key, model: type }, target);
    }
  };
}


/**
 * @summary Decorator to mark a class as a presenter type
 */
export function PresenterType(input: { key?: string, model: Function }) {

  return function (target: ComponentType<unknown>) 
  {     
    PresenterHelper.register(input, target);
  };
}

/**
 * @summary Pulls the type information from the presenterType decorator
 */
export class PresenterHelper {

  private static registry: PresenterRegistryItem[] = [];

  /**
   * Register the template with a model
   * @param metadata for the registration 
   * @param the component to register
   */
  public static register(input: PresenterModelKey, target: ComponentType<unknown>) {

    PresenterHelper.registry.push({ 
        key: input.key, 
        model: input.model, 
        component: target 
    });

    console.debug(`Registered presenter type for ${input.model.constructor.name} ${input.key ?? ''} with constructor ${target.name}`);
  }

  /**
   * Get a template for a model
   * @param lookup metadata
   * @returns the registered component
   */
  public static getTemplate(
    key: PresenterModelKey, 
    allowFallback: boolean): ComponentType<unknown> {
    if (key.key)
    {
      let registryMatch = PresenterHelper.findRegistryItemWithKey(key);    
      if (!registryMatch && allowFallback) registryMatch = PresenterHelper.findRegistryItemWithoutKey(key);

      if (!registryMatch) {
        registryMatch = PresenterHelper.findByPrototypeChain(key.model, key.key);
      }

      return registryMatch?.component;
    }
    else {
      let registryMatch = PresenterHelper.findRegistryItemWithoutKey(key);

      if (!registryMatch) {
        registryMatch = PresenterHelper.findByPrototypeChain(key.model);
      }
      return registryMatch?.component;
    }
  }

  private static findRegistryItemWithKey = (request:PresenterModelKey) =>
    PresenterHelper.registry.find(i=>
      i.key && request.key.toLowerCase() === i.key.toLowerCase() &&
      i.model === request.model.constructor);

  private static findRegistryItemWithoutKey = (request:PresenterModelKey) =>
    PresenterHelper.registry.find(i=> !i.key && i.model === request.model.constructor);


  private static findByPrototypeChain(model: Function, key?: string): PresenterRegistryItem | undefined {
    let proto = model.constructor?.prototype;

    while (proto) {
      const base = proto.constructor;
      const match = PresenterHelper.registry.find(
        (item) =>
          item.model === base &&
          ((key && item.key?.toLowerCase() === key.toLowerCase()) || (!key && !item.key))
      );

      if (match) return match;

      proto = Object.getPrototypeOf(proto);
    }

    return undefined;
  }
}

